import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, RotateCcw, Save, Search, UserRound } from 'lucide-react';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import { getThemeChromeButtonStyle, getThemeChromeTextStyle } from './modes/speedReadingShared';

type ReadingLevel = '5 ve 6. Sınıflar' | '7 ve 8. Sınıflar' | 'Lise' | 'Yetişkin' | 'İlkokul' | 'Ortaokul';
type TargetSpeedRange = '100-200' | '200-400' | '400-600' | '600+';
type AssessmentTextType = 'öykü' | 'makale';

type ManagedProfile = {
  id: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
  } | null;
  initialSpeed: number | null;
  currentSpeed: number | null;
  targetSpeedRange: TargetSpeedRange | null;
  targetSpeed: number | null;
  estimatedTargetDate: string | null;
  dailyReadingGoal: number | null;
  readingProfile: string | null;
  readingLevel: ReadingLevel | null;
  favoriteBookGenres: string[];
  onboardingCompleted: boolean;
  onboardingStep: number;
  readingComprehensionAssessment: {
    assessmentId?: string | null;
    age?: number | null;
    score?: number | null;
    recommendedReadingLevel?: ReadingLevel | null;
    textType?: string | null;
  } | null;
  updatedAt: string;
};

type ProfileDraft = {
  initialSpeed: string;
  currentSpeed: string;
  targetSpeedRange: TargetSpeedRange | '';
  targetSpeed: string;
  dailyReadingGoal: string;
  readingLevel: ReadingLevel | '';
  favoriteBookGenres: string[];
  onboardingCompleted: boolean;
  onboardingStep: string;
  assessmentId: string;
  assessmentAge: string;
  assessmentScore: string;
  assessmentRecommendedLevel: ReadingLevel | '';
  assessmentTextType: AssessmentTextType | '';
};

const READING_LEVEL_OPTIONS: ReadingLevel[] = ['İlkokul', 'Ortaokul', 'Lise'];
const TARGET_SPEED_RANGE_OPTIONS: TargetSpeedRange[] = ['100-200', '200-400', '400-600', '600+'];
const ASSESSMENT_TEXT_TYPE_OPTIONS: AssessmentTextType[] = ['öykü', 'makale'];
const GENRE_OPTIONS = [
  'Roman',
  'Bilim Kurgu',
  'Fantastik',
  'Polisiye / Dedektif',
  'Gerilim / Korku',
  'Macera',
  'Tarihi Roman',
  'Romantik / Aşk',
  'Dram',
  'Biyografi / Otobiyografi',
  'Kişisel Gelişim',
  'Psikoloji',
  'Felsefe',
  'Bilim / Popüler Bilim',
  'Makale / Deneme',
  'Çocuk Kitapları',
  'Şiir',
  'Hikâye (Öykü)',
];

const createEmptyDraft = (): ProfileDraft => ({
  initialSpeed: '',
  currentSpeed: '',
  targetSpeedRange: '',
  targetSpeed: '',
  dailyReadingGoal: '',
  readingLevel: '',
  favoriteBookGenres: [],
  onboardingCompleted: false,
  onboardingStep: '0',
  assessmentId: '',
  assessmentAge: '',
  assessmentScore: '',
  assessmentRecommendedLevel: '',
  assessmentTextType: '',
});

const buildDraftFromProfile = (profile: ManagedProfile | null): ProfileDraft => {
  if (!profile) return createEmptyDraft();
  return {
    initialSpeed: profile.initialSpeed ? String(profile.initialSpeed) : '',
    currentSpeed: profile.currentSpeed ? String(profile.currentSpeed) : '',
    targetSpeedRange: profile.targetSpeedRange || '',
    targetSpeed: profile.targetSpeed ? String(profile.targetSpeed) : '',
    dailyReadingGoal: profile.dailyReadingGoal ? String(profile.dailyReadingGoal) : '',
    readingLevel: profile.readingLevel || '',
    favoriteBookGenres: Array.isArray(profile.favoriteBookGenres) ? profile.favoriteBookGenres.slice(0, 3) : [],
    onboardingCompleted: Boolean(profile.onboardingCompleted),
    onboardingStep: String(profile.onboardingStep || 0),
    assessmentId: String(profile.readingComprehensionAssessment?.assessmentId || ''),
    assessmentAge: profile.readingComprehensionAssessment?.age ? String(profile.readingComprehensionAssessment.age) : '',
    assessmentScore: profile.readingComprehensionAssessment?.score !== null && profile.readingComprehensionAssessment?.score !== undefined
      ? String(profile.readingComprehensionAssessment.score)
      : '',
    assessmentRecommendedLevel: profile.readingComprehensionAssessment?.recommendedReadingLevel || '',
    assessmentTextType: (profile.readingComprehensionAssessment?.textType === 'öykü' || profile.readingComprehensionAssessment?.textType === 'makale')
      ? profile.readingComprehensionAssessment.textType
      : '',
  };
};

const InputField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'email';
  placeholder?: string;
}> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <label className="space-y-2">
    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full rounded-2xl border border-[#d9e5ea] bg-white px-4 py-3 text-sm text-[#234b5a] outline-none transition-colors focus:border-[var(--theme-primary)]"
    />
  </label>
);

const OptionGrid = <T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | '';
  options: readonly T[];
  onChange: (value: T | '') => void;
}) => (
  <div className="space-y-2">
    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">{label}</div>
    <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
      <button
        type="button"
        onClick={() => onChange('')}
        className="rounded-xl border px-3 py-3 text-sm font-semibold transition-colors"
        style={value === '' ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fbfdfe', color: '#58707b', borderColor: '#d7e4ea' }}
      >
        Boş
      </button>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className="rounded-xl border px-3 py-3 text-sm font-semibold transition-colors"
          style={value === option ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fbfdfe', color: '#58707b', borderColor: '#d7e4ea' }}
        >
          {option}
        </button>
      ))}
    </div>
  </div>
);

const ToggleCard: React.FC<{
  checked: boolean;
  title: string;
  description: string;
  onToggle: () => void;
}> = ({ checked, title, description, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full rounded-2xl border px-4 py-4 text-left transition-all"
    style={checked
      ? {
        backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, white)',
        borderColor: 'color-mix(in srgb, var(--theme-primary) 28%, #d7e4ea)',
      }
      : { backgroundColor: '#fbfdfe', borderColor: '#d7e4ea' }}
  >
    <div className="text-sm font-bold text-[#244c5b]">{title}</div>
    <div className="mt-1 text-xs text-[#6e858f]">{description}</div>
  </button>
);

const SpeedReadingUserSetupPanel: React.FC = () => {
  const { showConfirm, showToast, showAlert } = useModal();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState('');
  const [profiles, setProfiles] = useState<ManagedProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProfileDraft>(createEmptyDraft);
  const selectedProfileIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedProfileIdRef.current = selectedProfileId;
  }, [selectedProfileId]);

  const filteredProfiles = useMemo(() => profiles, [profiles]);

  const loadProfiles = useCallback(async (searchTerm = '') => {
    try {
      setLoading(true);
      setStatus('');
      const query = new URLSearchParams({
        includeIncomplete: 'true',
        limit: '200',
      });
      if (searchTerm.trim()) query.set('search', searchTerm.trim());

      const response = await ApiService.getApi(`speed-reading/admin/profiles?${query.toString()}`);
      const items = Array.isArray(response?.items) ? response.items : [];
      setProfiles(items);

      const selected = (selectedProfileIdRef.current && items.find((item: ManagedProfile) => item.id === selectedProfileIdRef.current)) || items[0] || null;
      setSelectedProfileId(selected?.id || null);
      setDraft(buildDraftFromProfile(selected));
    } catch (error) {
      console.error('Kullanıcı hızlı okuma profilleri yüklenemedi:', error);
      setProfiles([]);
      setSelectedProfileId(null);
      setDraft(createEmptyDraft());
      setStatus(error instanceof Error ? error.message : 'Kullanıcı profilleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      void loadProfiles(search);
    }, 250);
    return () => clearTimeout(timeout);
  }, [loadProfiles, search]);

  const selectedProfile = useMemo(
    () => filteredProfiles.find((item) => item.id === selectedProfileId) || null,
    [filteredProfiles, selectedProfileId]
  );

  useEffect(() => {
    setDraft(buildDraftFromProfile(selectedProfile));
  }, [selectedProfile]);

  const toggleGenre = (genre: string) => {
    setDraft((prev) => {
      if (prev.favoriteBookGenres.includes(genre)) {
        return { ...prev, favoriteBookGenres: prev.favoriteBookGenres.filter((item) => item !== genre) };
      }
      if (prev.favoriteBookGenres.length >= 3) return prev;
      return { ...prev, favoriteBookGenres: [...prev.favoriteBookGenres, genre] };
    });
  };

  const handleSave = async () => {
    if (!selectedProfile) return;
    try {
      setSaving(true);
      const payload = {
        initialSpeed: draft.initialSpeed ? Number(draft.initialSpeed) : null,
        currentSpeed: draft.currentSpeed ? Number(draft.currentSpeed) : null,
        targetSpeedRange: draft.targetSpeedRange || null,
        targetSpeed: draft.targetSpeed ? Number(draft.targetSpeed) : null,
        dailyReadingGoal: draft.dailyReadingGoal ? Number(draft.dailyReadingGoal) : null,
        readingLevel: draft.readingLevel || null,
        favoriteBookGenres: draft.favoriteBookGenres.length > 0 ? draft.favoriteBookGenres : null,
        onboardingCompleted: draft.onboardingCompleted,
        onboardingStep: Number(draft.onboardingStep || 0),
        readingComprehensionAssessment: [
          draft.assessmentId,
          draft.assessmentAge,
          draft.assessmentScore,
          draft.assessmentRecommendedLevel,
          draft.assessmentTextType,
        ].some(Boolean) ? {
          assessmentId: draft.assessmentId || null,
          age: draft.assessmentAge ? Number(draft.assessmentAge) : null,
          score: draft.assessmentScore ? Number(draft.assessmentScore) : null,
          recommendedReadingLevel: draft.assessmentRecommendedLevel || null,
          textType: draft.assessmentTextType || null,
        } : null,
      };

      const response = await ApiService.putApi(`speed-reading/admin/profiles/${selectedProfile.id}`, payload);
      const saved = response?.item as ManagedProfile;
      setStatus('Kullanıcı başlangıç kurulumu güncellendi.');
      setProfiles((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
      setSelectedProfileId(saved.id);
      setDraft(buildDraftFromProfile(saved));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Kullanıcı profili güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProfile) return;
    const approved = await showConfirm({
      title: 'Kurulumu Sıfırla',
      message: 'Bu kullanıcının hızlı okuma başlangıç kurulumu sıfırlanacak. Kullanıcı tekrar onboarding sürecine döner. Devam etmek istiyor musunuz?',
      confirmText: 'Sıfırla',
      confirmButtonVariant: 'danger',
    });
    if (!approved) return;

    try {
      setDeleting(true);
      await ApiService.deleteApi(`speed-reading/admin/profiles/${selectedProfile.id}`);
      setStatus('Kullanıcı başlangıç kurulumu sıfırlandı.');
      showToast('Kullanıcı başlangıç kurulumu sıfırlandı.', 'success');
      await loadProfiles(search);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Kullanıcı başlangıç kurulumu silinemedi.';
      setStatus(message);
      await showAlert({
        title: 'Sıfırlama Hatası',
        message,
        type: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-[#f3f5f7] p-3 sm:p-4 md:h-full md:min-h-0 lg:px-6 lg:pt-6 lg:pb-6">
      <div className="mx-auto flex max-w-[1480px] flex-col rounded-[24px] border border-[#e0e9ed] bg-[#f8fafb] p-4 shadow-sm sm:rounded-[28px] sm:p-5 md:h-full md:min-h-0">
        <div className="mb-5 flex shrink-0 flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[22px] font-black sm:text-[28px]" style={getThemeChromeTextStyle()}>Kullanıcı Düzenleme</div>
            <p className="mt-1 max-w-[720px] text-sm leading-6 text-[#6f8590]">Öğretmenler bu alandan öğrencinin başlangıç kurulumunda seçtiği hızlı okuma verilerini düzenleyebilir veya profili tamamen sıfırlayabilir.</p>
          </div>
        </div>

        <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col rounded-[22px] border border-[#dce7ec] bg-white p-4 sm:rounded-[24px]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="text-sm font-black text-[#214858]">Kullanıcı Profilleri</div>
              <button onClick={() => void loadProfiles(search)} className="rounded-xl border border-[#dbe6eb] bg-white p-2 text-[#5a727d]"><RefreshCw className="h-4 w-4" /></button>
            </div>

            <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#dce5ea] bg-[#fbfdfe] px-3 py-2">
              <Search className="h-4 w-4 text-[#89a1ac]" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="Kullanıcı ara..." />
            </div>

            <div className="max-h-[380px] overflow-y-auto pr-1 md:min-h-0 md:flex-1 md:max-h-none">
              {loading ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6f8792]" /></div>
              ) : filteredProfiles.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#d7e3e8] bg-[#fbfdfe] px-4 py-6 text-sm text-[#70858e]">Bu alanda hızlı okuma kurulumu bulunan kullanıcı yok.</div>
              ) : (
                <div className="space-y-3">
                  {filteredProfiles.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedProfileId(item.id)}
                      className="w-full rounded-2xl border px-4 py-4 text-left"
                      style={selectedProfileId === item.id ? { borderColor: 'var(--theme-primary)', backgroundColor: '#f4fbfe' } : { borderColor: '#e1ebef', backgroundColor: '#fff' }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-full bg-[#eef5f8] p-2 text-[#476572]"><UserRound className="h-4 w-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-[#244c5b]">{item.user?.fullName || 'İsimsiz Kullanıcı'}</div>
                          <div className="mt-1 truncate text-xs text-[#708690]">{item.user?.email || 'E-posta bilgisi yok'}</div>
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold text-[#8aa0a9]">
                            <span>{item.onboardingCompleted ? 'Kurulum Tamamlandı' : 'Kurulum Eksik'}</span>
                            {item.readingLevel ? <span>· {item.readingLevel}</span> : null}
                            <span>· Adım {item.onboardingStep || 0}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-col rounded-[22px] border border-[#dce7ec] bg-white p-4 sm:rounded-[24px] sm:p-5">
            <div className="mb-4 flex shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0">
                <div className="text-base font-black text-[#214858] sm:text-lg">Başlangıç Kurulumunu Düzenle</div>
                <p className="mt-1 text-sm text-[#6f8590]">Seçilen kullanıcının onboarding verilerini güncelle veya tamamen sıfırla.</p>
                {selectedProfile?.user ? (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#7a8e98]">
                    <span className="font-semibold text-[#325867]">{selectedProfile.user.fullName}</span>
                    <span>{selectedProfile.user.email}</span>
                  </div>
                ) : null}
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={!selectedProfile || deleting || saving}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#f1c9c9] px-4 py-3 text-sm font-semibold text-[#c44f4f] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Sıfırla
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!selectedProfile || saving || deleting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  style={getThemeChromeButtonStyle()}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Kaydet
                </button>
              </div>
            </div>

            {status ? (
              <div className="mb-4 rounded-2xl border border-[#dce7ec] bg-[#fbfdfe] px-4 py-3 text-sm text-[#55707c]">{status}</div>
            ) : null}

            {!selectedProfile ? (
              <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-dashed border-[#d7e3e8] bg-[#fbfdfe] px-6 text-center text-sm text-[#70858e]">
                Soldan bir kullanıcı seçildiğinde başlangıç kurulum ayarları burada açılır.
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="space-y-5">
                  <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-[#8aa0a9]">
                    <span className="rounded-full border border-[#dde7ec] bg-[#f8fbfc] px-3 py-1.5">Profil: {selectedProfile.readingProfile || '-'}</span>
                    <span className="rounded-full border border-[#dde7ec] bg-[#f8fbfc] px-3 py-1.5">Güncellendi: {new Date(selectedProfile.updatedAt).toLocaleString('tr-TR')}</span>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <InputField label="Başlangıç Hızı" type="number" value={draft.initialSpeed} onChange={(value) => setDraft((prev) => ({ ...prev, initialSpeed: value }))} placeholder="200" />
                    <InputField label="Güncel Hız" type="number" value={draft.currentSpeed} onChange={(value) => setDraft((prev) => ({ ...prev, currentSpeed: value }))} placeholder="260" />
                    <InputField label="Hedef Hız" type="number" value={draft.targetSpeed} onChange={(value) => setDraft((prev) => ({ ...prev, targetSpeed: value }))} placeholder="400" />
                    <InputField label="Günlük Okuma Hedefi" type="number" value={draft.dailyReadingGoal} onChange={(value) => setDraft((prev) => ({ ...prev, dailyReadingGoal: value }))} placeholder="20" />
                    <InputField label="Onboarding Adımı" type="number" value={draft.onboardingStep} onChange={(value) => setDraft((prev) => ({ ...prev, onboardingStep: value }))} placeholder="12" />
                    <InputField label="Seviye Testi Yaşı" type="number" value={draft.assessmentAge} onChange={(value) => setDraft((prev) => ({ ...prev, assessmentAge: value }))} placeholder="12" />
                    <InputField label="Seviye Testi Puanı" type="number" value={draft.assessmentScore} onChange={(value) => setDraft((prev) => ({ ...prev, assessmentScore: value }))} placeholder="80" />
                  </div>

                  <OptionGrid label="Hedef Hız Aralığı" value={draft.targetSpeedRange} options={TARGET_SPEED_RANGE_OPTIONS} onChange={(value) => setDraft((prev) => ({ ...prev, targetSpeedRange: value as TargetSpeedRange | '' }))} />
                  <OptionGrid label="Okuma Seviyesi" value={draft.readingLevel} options={READING_LEVEL_OPTIONS} onChange={(value) => setDraft((prev) => ({ ...prev, readingLevel: value as ReadingLevel | '' }))} />
                  <OptionGrid label="Seviye Testi Önerilen Seviye" value={draft.assessmentRecommendedLevel} options={READING_LEVEL_OPTIONS} onChange={(value) => setDraft((prev) => ({ ...prev, assessmentRecommendedLevel: value as ReadingLevel | '' }))} />
                  <OptionGrid label="Seviye Testi Metin Türü" value={draft.assessmentTextType} options={ASSESSMENT_TEXT_TYPE_OPTIONS} onChange={(value) => setDraft((prev) => ({ ...prev, assessmentTextType: value as AssessmentTextType | '' }))} />

                  <ToggleCard
                    checked={draft.onboardingCompleted}
                    title="Onboarding Tamamlandı"
                    description="Pasif yapılırsa kullanıcı tekrar başlangıç kurulum akışına döner."
                    onToggle={() => setDraft((prev) => ({ ...prev, onboardingCompleted: !prev.onboardingCompleted }))}
                  />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8e98]">Favori Kitap Türleri</div>
                      <div className="text-[11px] font-semibold text-[#8aa0a9]">{draft.favoriteBookGenres.length}/3 seçildi</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {GENRE_OPTIONS.map((genre) => {
                        const active = draft.favoriteBookGenres.includes(genre);
                        const disabled = !active && draft.favoriteBookGenres.length >= 3;
                        return (
                          <button
                            key={genre}
                            type="button"
                            onClick={() => toggleGenre(genre)}
                            disabled={disabled}
                            className="rounded-xl border px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45"
                            style={active ? { ...getThemeChromeButtonStyle(), color: '#fff', borderColor: 'transparent' } : { backgroundColor: '#fbfdfe', color: '#58707b', borderColor: '#d7e4ea' }}
                          >
                            {genre}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedReadingUserSetupPanel;
